// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Registrare una consegna"
// Centro assistenza → guida per operatori e amministratori, solo piano TRACE.
//
// Copre l'intero gesto del ricevimento: il modello di accettazione (che si
// imposta una volta), la bolla di ingresso, i controlli, l'esito, la non
// conformita' che nasce da una consegna e il registro da stampare.
//
// Il magazzino materie prime — cosa succede ai lotti DOPO — sta nella guida
// successiva. La linea di taglio e': qui si riceve, li' si gestisce la scorta.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice, non scritte a memoria.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Modelli ............... App.js, GestioneModelliAccettazione
//                           tipi in ACC_TIPI: 'cn' | 'numero' | 'flag'
//   Regola dell'esito ..... App.js, accEsitoVoce + accEsitoComplessivo
//                           NC se almeno una voce e' NC; C se almeno una e'
//                           valutata C; altrimenti nessun esito.
//   Bolla ................. App.js, GestioneBolleIngresso (form + dettaglio)
//                           sezioni "Intestazione bolla", "Controlli al
//                           ricevimento", "Articoli ricevuti"
//   Fornitore/committente . dal 18/08/2026 CONVIVONO. Il fornitore e'
//                           obbligatorio (anche backend, POST e PUT
//                           /bolle-ingresso), il committente e' facoltativo.
//                           Se il selettore "Tipo ingresso" ricompare da
//                           qualche parte, il capitolo 3 va riscritto.
//   NC automatica ......... App.js, proponiNC → conferma "Esito NON CONFORME"
//   Lotto interno ......... backend/routes/tracciabilita.js, generaLottoInterno
//                           formato L.GGMMAA, duplicati come L.GGMMAA.001
//   Registro .............. App.js, GestioneRicevimentoMP → "Stampa registro"
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'a0', label: 'Prima di iniziare' },
  { id: 'a1', label: '1. Il modello dei controlli' },
  { id: 'a2', label: '2. Registrare la consegna' },
  { id: 'a3', label: '3. Chi consegna, di chi è' },
  { id: 'a4', label: '4. I controlli e l’esito' },
  { id: 'a5', label: '5. Gli articoli e i lotti' },
  { id: 'a6', label: '6. Quando è Non Conforme' },
  { id: 'a7', label: '7. Il registro da stampare' },
  { id: 'a8', label: 'Le trappole' },
  { id: 'a9', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const BtnFinto = ({ children, kind = 'neutro' }) => {
  const c = {
    blu:    { bg: LT.blu,   fg: '#fff',  bd: LT.blu },
    verde:  { bg: LT.verde, fg: '#fff',  bd: LT.verde },
    neutro: { bg: LT.n0,    fg: LT.n700, bd: LT.n300 },
  }[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '5px 11px', borderRadius: LT.r.md, fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const CampoFinto = ({ etichetta, valore, placeholder, larghezza }) => (
  <div style={{ width: larghezza || '100%' }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || placeholder}</div>
  </div>
);

// Una riga della checklist di accettazione, come compare nel form della bolla.
const VoceControllo = ({ label, children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    padding: '8px 10px', borderRadius: LT.r.md,
    background: LT.n50, border: `1px solid ${LT.n150}`, marginBottom: 6,
  }}>
    <div style={{ flex: '1 1 190px', fontSize: 12.5, color: LT.navy, fontWeight: 600 }}>
      {label}
    </div>
    {children}
  </div>
);

const SegmentoCN = ({ scelto }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {[['Conforme', LT.verde], ['Non conf.', LT.rosso]].map(([lab, col]) => {
      const on = lab === scelto;
      return (
        <span key={lab} style={{
          padding: '5px 11px', borderRadius: LT.r.md, fontSize: 12, fontWeight: 600,
          border: `1.5px solid ${on ? col : LT.n200}`,
          background: on ? col : LT.n0, color: on ? '#fff' : LT.navy,
        }}>{lab}</span>
      );
    })}
  </div>
);

const SezioneFinta = ({ colore = LT.blu, titolo, sotto, children }) => (
  <div style={{
    background: LT.n0, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
    padding: 12, marginBottom: 10, borderLeft: `3px solid ${colore}`,
  }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy }}>{titolo}</div>
    {sotto && (
      <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 2, marginBottom: 10 }}>{sotto}</div>
    )}
    {children}
  </div>
);

export default function Accettazioni() {
  return (
    <GuidaLayout
      titolo="Registrare una consegna"
      sottotitolo="Ogni volta che arriva merce si registra una bolla di ingresso: chi ve l'ha portata, cosa contiene, com'era quando è arrivata. È il primo anello della catena — se questo si registra bene, tutto il resto viene da sé."
      durata="~ 15 minuti"
      destinatario="Per operatori e amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* a0 */}
      <H id="a0">Prima di iniziare</H>

      <P>
        Quando arriva un camion succedono due cose insieme, e il programma le registra in un
        gesto solo. La prima è <Strong>l'ingresso della merce</Strong>: cosa è entrato, in che
        quantità, con che lotto. La seconda è <Strong>l'accettazione</Strong>: il controllo che
        fate al ricevimento — temperatura, integrità delle confezioni, documenti — e che l'HACCP
        vi chiede di dimostrare.
      </P>
      <P>
        Sono due obblighi diversi che nascono dallo stesso momento. Per questo in LabThree
        si compilano nella stessa schermata: <Code>Bolle di ingresso</Code>, dal menù
        Tracciabilità, gruppo <Strong>Entrate</Strong>.
      </P>

      <Callout kind="info" titolo="Cosa deve esistere prima">
        Il <Strong>fornitore</Strong> in anagrafica (ma potete crearlo al volo dalla bolla
        stessa) e gli <Strong>ingredienti</Strong> che state ricevendo. Se la tendina degli
        ingredienti è vuota, il problema non è qui: è che l'anagrafica ingredienti è ancora da
        compilare.
      </Callout>

      <P>
        Il modello dei controlli — la checklist dell'accettazione — si imposta invece{' '}
        <Strong>una volta sola</Strong>, e poi torna in ogni bolla. È il primo capitolo, ma è
        anche l'unico che farete una volta e non più.
      </P>

      {/* a1 */}
      <H id="a1" cat="formazione">1. Il modello dei controlli</H>

      <P>
        Da <Code>Tracciabilità</Code> · <Code>Configurazione</Code> ·{' '}
        <Code>Modelli di accettazione</Code>, il pulsante <Tasto>Nuovo modello</Tasto>.
      </P>
      <P>
        Un modello è la vostra checklist di ricevimento: l'elenco delle cose che guardate
        quando scaricate. Potete averne più di uno — è normale che la carne fresca abbia
        controlli diversi dai cartoni — e in bolla si sceglie quale applicare.
      </P>

      <MockFrame
        url="app.labthree.it — Modelli di accettazione"
        markers={[
          { n: 1, top: '22%', left: '50%', titolo: 'Il nome',
            testo: 'È quello che vedrete nella tendina in bolla. Se ne avete più di uno, chiamateli come la merce a cui si applicano.' },
          { n: 2, top: '55%', left: '22%', titolo: 'Il tipo di controllo',
            testo: 'Decide come si compila e come pesa sull’esito. Sono tre e sono spiegati qui sotto.' },
          { n: 3, top: '80%', left: '78%', titolo: 'Obbligatorio',
            testo: 'Spuntato di suo. Determina se una voce lasciata in bianco fa scattare il Non Conforme.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo
            titolo="Nuovo modello di accettazione"
            sottotitolo="La checklist che comparirà in ogni bolla di ingresso"
          />
          <div style={{ marginBottom: 12 }}>
            <CampoFinto etichetta="Nome modello *" valore="Carne fresca" />
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 8,
          }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: LT.navy }}>Controlli della checklist</div>
            <BtnFinto>+ Aggiungi controllo</BtnFinto>
          </div>

          {[
            { n: 1, label: 'Conformità documenti / DDT', tipo: 'Conforme / Non Conforme' },
            { n: 2, label: 'Temperatura al ricevimento',  tipo: 'Valore numerico (es. temperatura)', extra: '°C · min 0 / max 4' },
            { n: 3, label: 'Prelievo controcampione',     tipo: 'Sì / No (es. controcampione)' },
          ].map(v => (
            <div key={v.n} style={{
              border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
              padding: 9, marginBottom: 7, background: LT.n0,
            }}>
              <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 7 }}>
                <span style={{
                  width: 20, height: 20, borderRadius: '50%', background: LT.bluSoft, color: LT.blu,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 10.5, flexShrink: 0,
                }}>{v.n}</span>
                <div style={{
                  flex: 1, border: `1px solid ${LT.n300}`, borderRadius: LT.r.md,
                  padding: '6px 8px', fontSize: 12.5, color: LT.n900, background: LT.n0,
                }}>{v.label}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{
                  flex: '1 1 200px', border: `1px solid ${LT.n300}`, borderRadius: LT.r.md,
                  padding: '6px 8px', fontSize: 12, color: LT.n700, background: LT.n0,
                }}>{v.tipo}</div>
                {v.extra && (
                  <div style={{ fontSize: 11.5, color: LT.n500, whiteSpace: 'nowrap' }}>{v.extra}</div>
                )}
                <span style={{ fontSize: 11.5, color: LT.n600, whiteSpace: 'nowrap' }}>
                  ☑ Obbligatorio
                </span>
              </div>
            </div>
          ))}
        </Schermo>
      </MockFrame>

      <P>I tre tipi di controllo, e come si comportano:</P>

      <Passo n="1" titolo="Conforme / Non Conforme">
        <P style={{ margin: '4px 0' }}>
          Due pulsanti, <Tasto>Conforme</Tasto> e <Tasto>Non conf.</Tasto>. Il più usato: va bene
          per tutto quello che si giudica a occhio — documenti, pulizia del mezzo, integrità dei
          colli. Lasciato in bianco non viene valutato.
        </P>
      </Passo>

      <Passo n="2" titolo="Valore numerico">
        <P style={{ margin: '4px 0' }}>
          Si scrive un numero e si può indicare un'unità di misura e due soglie,{' '}
          <Code>min</Code> e <Code>max</Code>. Se il valore esce dalle soglie, la voce diventa
          automaticamente Non Conforme e compare la scritta rossa{' '}
          <ChipFinto tono="rosso">fuori soglia</ChipFinto> accanto al campo. Le soglie sono
          facoltative: senza, il numero viene solo registrato.
        </P>
      </Passo>

      <Passo n="3" titolo="Sì / No">
        <P style={{ margin: '4px 0' }}>
          Una casella da spuntare. Serve per i fatti, non per i giudizi: "prelevato il
          controcampione", "scaricato in cella". Qui la spunta{' '}
          <Strong>Obbligatorio</Strong> cambia parecchio, ed è la cosa meno evidente di tutta
          la schermata.
        </P>
      </Passo>

      <Callout kind="warn" titolo="Cosa fa davvero “Obbligatorio” su un Sì / No">
        Se la voce è obbligatoria, <Strong>lasciarla senza spunta vale Non Conforme</Strong>, non
        "non compilato". È giusto per un requisito vero — "confezioni integre" lasciato a No deve
        pesare — ma è sbagliato per un promemoria facoltativo, che vi farebbe risultare non
        conforme ogni volta che non lo spuntate. Regola pratica: se è una cosa che <em>deve</em>{' '}
        essere vera, lasciate la spunta; se è solo un'annotazione, toglietela.
      </Callout>

      <Callout kind="tip" titolo="Non dovete indovinarlo adesso">
        Un modello si modifica quando volete, e le bolle già registrate non cambiano: conservano
        i controlli come erano al momento della registrazione. Quindi partite con quattro o
        cinque voci, e aggiustate dopo un mese di uso reale.
      </Callout>

      <P>
        Un modello che non usate più non va cancellato: si toglie la spunta{' '}
        <Strong>Modello attivo</Strong> e sparisce dalla tendina restando nello storico. Nella
        lista lo ritrovate marcato <ChipFinto>Archiviato</ChipFinto>.
      </P>

      {/* a2 */}
      <H id="a2" cat="temperature">2. Registrare la consegna</H>

      <P>
        Da <Code>Bolle di ingresso</Code>, il pulsante <Tasto>Nuova bolla</Tasto>. Il form ha tre
        sezioni, nell'ordine in cui conviene compilarle: l'intestazione, i controlli, gli
        articoli.
      </P>

      <MockFrame
        url="app.labthree.it — Nuova bolla di ingresso"
        markers={[
          { n: 1, top: '30%', left: '20%', titolo: 'Data documento (DDT)',
            testo: 'Obbligatoria, ed è quella scritta sul documento del fornitore — non oggi. Da questa data nasce il lotto interno.' },
          { n: 2, top: '30%', left: '80%', titolo: 'Data ricevimento',
            testo: 'Il giorno in cui la merce è arrivata da voi. Di solito coincide, ma se il DDT è di ieri va lasciata diversa.' },
          { n: 3, top: '62%', left: '50%', titolo: 'Fornitore',
            testo: 'Chi ve l’ha materialmente consegnata. Obbligatorio. Se non è in anagrafica, Nuovo lo crea senza uscire da qui.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo
            titolo="Nuova bolla di ingresso"
            sottotitolo="Registra la merce in ingresso: chi te l'ha consegnata e, se è conto lavorazione, di chi è"
          />
          <SezioneFinta
            colore={LT.blu}
            titolo="Intestazione bolla"
            sotto="Numero documento, data, chi consegna e di chi è la merce"
          >
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <CampoFinto etichetta="N° bolla / DDT" valore="2026/418" larghezza="30%" />
              <CampoFinto etichetta="Data documento (DDT) *" valore="17/08/2026" larghezza="30%" />
              <CampoFinto etichetta="Data ricevimento" valore="18/08/2026" larghezza="30%" />
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3,
            }}>
              <div style={{
                fontSize: 10.5, fontWeight: 700, color: LT.n600,
                textTransform: 'uppercase', letterSpacing: 0.3,
              }}>Fornitore — chi ti ha consegnato la merce *</div>
              <BtnFinto>+ Nuovo</BtnFinto>
            </div>
            <div style={{
              border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
              background: LT.n0, fontSize: 13, color: LT.n900, marginBottom: 10,
            }}>Mec Carni S.p.A.</div>
            <CampoFinto etichetta="Committente — di chi è la merce" placeholder="— la merce è mia —" />
          </SezioneFinta>
        </Schermo>
      </MockFrame>

      <Callout kind="warn" titolo="La data che conta è quella del DDT">
        Il <Strong>lotto interno</Strong> viene generato dalla{' '}
        <Strong>data del documento</Strong>, non da quella di ricevimento. Se mettete oggi su un
        DDT di ieri, il lotto nasce con la data sbagliata e resta così per sempre nei documenti
        di rintracciabilità. Sono due campi separati proprio per questo.
      </Callout>

      {/* a3 */}
      <H id="a3" cat="temperature">3. Chi consegna e di chi è la merce</H>

      <P>
        Due campi vicini che rispondono a due domande diverse. <Strong>Fornitore</Strong> è chi
        vi ha portato la merce — il macello, il grossista, il corriere del mittente.{' '}
        <Strong>Committente</Strong> è di chi <em>è</em> la merce, se non è vostra.
      </P>
      <P>
        Nella stragrande maggioranza dei casi la merce è vostra: l'avete comprata. Allora il
        committente si lascia vuoto — la tendina dice esattamente questo,{' '}
        <Tasto>— la merce è mia —</Tasto> — e non dovete pensarci più.
      </P>

      <Callout kind="info" titolo="Il conto lavorazione">
        Se invece lavorate <Strong>a balia</Strong> — ricevete merce che resta di qualcun altro,
        la lavorate e gliela restituite — allora il committente è il proprietario. Il caso
        tipico: un prosciuttificio riceve le cosce da un macello, ma le cosce sono di un
        committente terzo che poi se le riprende stagionate. Il fornitore è il macello, il
        committente è il proprietario, e servono tutti e due: il macello può cambiare a ogni
        consegna mentre il committente resta lo stesso.
      </Callout>

      <P>
        Scelto un committente, sotto la tendina compare subito il contrassegno arancione{' '}
        <ChipFinto tono="arancio">Conto terzi · nome</ChipFinto>, così vedete cosa state
        registrando prima di salvare. Da lì in poi quel contrassegno segue la merce: lo
        ritrovate sui lotti in magazzino, nelle giacenze e sul prodotto finito. Serve a non
        confondere in cella quello che è vostro con quello che dovete restituire.
      </P>

      <Callout kind="tip" titolo="Se è il committente stesso a consegnare">
        Capita: il proprietario vi manda la merce con i suoi mezzi. Il fornitore resta comunque
        obbligatorio, quindi inseritelo anche fra i fornitori con <Tasto>Nuovo</Tasto> — sono
        cinque secondi e non serve uscire dal form. Sembra una ripetizione, ma tiene distinte
        due informazioni che in un richiamo servono separate.
      </Callout>

      {/* a4 */}
      <H id="a4" cat="temperature">4. I controlli e l'esito</H>

      <P>
        La seconda sezione, <Strong>Controlli al ricevimento</Strong>, mostra le voci del modello
        che avete scelto. Se avete un solo modello viene applicato da sé e non vedete nemmeno la
        tendina.
      </P>

      <MockFrame
        url="app.labthree.it — Controlli al ricevimento"
        markers={[
          { n: 1, top: '35%', left: '80%', titolo: 'Un valore fuori soglia',
            testo: 'Il programma non ve lo impedisce: lo registra e lo segna. Registrare il vero è il punto di tutto il modulo.' },
          { n: 2, top: '82%', left: '35%', titolo: 'Esito complessivo',
            testo: 'Si aggiorna mentre compilate. Basta una voce Non Conforme perché tutta la consegna lo diventi.' },
        ]}
      >
        <Schermo>
          <SezioneFinta
            colore={LT.arancio}
            titolo="Controlli al ricevimento"
            sotto="Esito dei controlli di accettazione secondo il modello scelto"
          >
            <VoceControllo label="Conformità documenti / DDT *">
              <SegmentoCN scelto="Conforme" />
            </VoceControllo>
            <VoceControllo label="Temperatura al ricevimento *">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  border: `1px solid ${LT.n300}`, borderRadius: LT.r.md,
                  padding: '5px 8px', fontSize: 12.5, background: LT.n0, color: LT.n900,
                }}>6,2</span>
                <span style={{ fontSize: 12, color: LT.n500 }}>°C</span>
                <span style={{ fontSize: 11.5, color: LT.n400 }}>(0/4)</span>
                <ChipFinto tono="rosso">fuori soglia</ChipFinto>
              </div>
            </VoceControllo>
            <VoceControllo label="Prelievo controcampione">
              <span style={{ fontSize: 12.5, color: LT.n700 }}>☐ Sì</span>
            </VoceControllo>

            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: LT.n500, fontWeight: 600 }}>Esito complessivo:</span>
              <ChipFinto tono="rosso">NON CONFORME</ChipFinto>
            </div>
          </SezioneFinta>
        </Schermo>
      </MockFrame>

      <P>La regola dell'esito complessivo è breve e vale sempre:</P>

      <div style={{
        border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
        padding: '14px 18px', margin: '18px 0', background: LT.n0,
      }}>
        {[
          ['Anche una sola voce Non Conforme', 'tutta la consegna è NON CONFORME', 'rosso'],
          ['Nessuna NC, almeno una voce valutata', 'la consegna è CONFORME', 'verde'],
          ['Nessuna voce compilata', 'nessun esito: la bolla resta senza giudizio', 'neutro'],
        ].map(([caso, esito, tono], i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            padding: '8px 0',
            borderBottom: i < 2 ? `1px solid ${LT.n150}` : 'none',
          }}>
            <span style={{ flex: '1 1 240px', fontSize: 14, color: LT.n700 }}>{caso}</span>
            <span style={{ flex: '1 1 220px', fontSize: 14, color: LT.n800, fontWeight: 600 }}>
              <ChipFinto tono={tono}>→</ChipFinto> {esito}
            </span>
          </div>
        ))}
      </div>

      <Callout kind="warn" titolo="Un controllo lasciato in bianco non è un controllo passato">
        Se compilate solo metà checklist, l'esito si basa su quella metà. Non è un errore del
        programma, ma davanti a un ispettore una consegna "conforme" con tre voci vuote è
        difficile da difendere. La checklist ha senso se la compilate tutta, ogni volta.
      </Callout>

      {/* a5 */}
      <H id="a5" cat="temperature">5. Gli articoli e i lotti</H>

      <P>
        L'ultima sezione, <Strong>Articoli ricevuti</Strong>. Una riga per ogni cosa arrivata:
        ingrediente, lotto del fornitore, quantità, unità di misura, scadenza, note.{' '}
        <Tasto>Aggiungi riga</Tasto> ne mette un'altra.
      </P>

      <MockFrame
        url="app.labthree.it — Articoli ricevuti"
        didascalia="Ogni riga diventa un lotto in magazzino. Il campo Lotto interno lasciato vuoto viene generato dal programma."
      >
        <Schermo>
          <SezioneFinta
            colore={LT.verde}
            titolo="Articoli ricevuti"
            sotto="Aggiungi le righe della bolla con ingrediente, lotto, quantità e scadenza"
          >
            <div style={{
              background: LT.verdeSoft, border: `1px solid ${LT.verde}30`,
              borderRadius: LT.r.md, padding: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{
                  width: 20, height: 20, borderRadius: '50%', background: LT.verde, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 10.5,
                }}>1</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: LT.verde }}>Articolo 1</span>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 6 }}>
                {[
                  ['Coscia suina fresca', '1 1 170px', LT.n900],
                  ['Lotto fornitore', '1 1 115px', LT.n400],
                  ['Lotto interno (auto)', '1 1 115px', LT.n400],
                ].map(([t, f, col]) => (
                  <div key={t} style={{
                    flex: f, border: `1px solid ${LT.n300}`, borderRadius: LT.r.md,
                    padding: '6px 8px', fontSize: 12, background: LT.n0, color: col,
                  }}>{t}</div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                {[['1 240', '0 0 66px', LT.n900], ['kg', '0 0 54px', LT.n900]].map(([t, f, col]) => (
                  <div key={t} style={{
                    flex: f, border: `1px solid ${LT.n300}`, borderRadius: LT.r.md,
                    padding: '6px 8px', fontSize: 12, background: LT.n0, color: col,
                  }}>{t}</div>
                ))}
                <span style={{ fontSize: 11, color: LT.n500, fontWeight: 600 }}>Scad.</span>
                <div style={{
                  flex: '0 0 110px', border: `1px solid ${LT.n300}`, borderRadius: LT.r.md,
                  padding: '6px 8px', fontSize: 12, background: LT.n0, color: LT.n900,
                }}>30/08/2026</div>
                <div style={{
                  flex: '1 1 90px', border: `1px solid ${LT.n300}`, borderRadius: LT.r.md,
                  padding: '6px 8px', fontSize: 12, background: LT.n0, color: LT.n400,
                }}>Note</div>
              </div>
            </div>
            <div style={{ marginTop: 10 }}><BtnFinto>+ Aggiungi riga</BtnFinto></div>
          </SezioneFinta>
        </Schermo>
      </MockFrame>

      <P>
        Ci sono <Strong>due lotti</Strong> su ogni riga, e confonderli è l'errore più comune di
        tutta la schermata.
      </P>

      <Passo n="1" titolo="Lotto fornitore">
        <P style={{ margin: '4px 0' }}>
          È il lotto <Strong>scritto dal fornitore</Strong> sul DDT o sull'etichetta. Voi lo
          copiate e basta. È quello che serve per risalire a monte: se domani il macello richiama
          un lotto, è questo il numero che vi comunica.
        </P>
      </Passo>

      <Passo n="2" titolo="Lotto interno">
        <P style={{ margin: '4px 0' }}>
          È il <Strong>vostro</Strong> nome per quella merce, quello che userete in produzione.
          Lasciando il campo vuoto lo genera il programma nel formato{' '}
          <Code>L.GGMMAA</Code> — <Code>L.170826</Code> per un DDT del 17 agosto 2026. Se lo
          stesso ingrediente arriva due volte con la stessa data, il secondo diventa{' '}
          <Code>L.170826.001</Code>, così non ci sono mai due lotti uguali.
        </P>
      </Passo>

      <Callout kind="tip" titolo="Potete scriverlo voi">
        Se avete già una vostra codifica dei lotti, scrivetela nel campo e il programma la
        rispetta senza generare nulla. Non siete costretti ad adottare il formato di LabThree.
      </Callout>

      <P>
        Poi <Tasto>Salva bolla</Tasto>. Ogni riga diventa un lotto in{' '}
        <Code>Magazzino materie prime</Code>, con stato <ChipFinto tono="blu">In magazzino</ChipFinto>,
        pronto per essere usato in una lavorazione.
      </P>

      {/* a6 */}
      <H id="a6" cat="pulizie">6. Quando l'esito è Non Conforme</H>

      <P>
        Salvando una bolla con esito NC, il programma si ferma e chiede:{' '}
        <Strong>Esito NON CONFORME — Sono presenti controlli non conformi al ricevimento. Vuoi
        aprire una Non Conformità nel registro?</Strong>
      </P>
      <P>
        Se rispondete sì, la NC nasce già compilata: descrive l'accettazione non conforme, cita
        il fornitore e il numero di DDT, elenca quali controlli sono falliti, e resta agganciata
        alla bolla. Da quel momento la gestite dal <Code>Registro non conformità</Code> come
        qualunque altra — causa, trattamento, azione correttiva.
      </P>

      <Callout kind="info" titolo="Potete anche dire di no">
        Non tutte le non conformità di accettazione meritano una pratica aperta. Una temperatura
        di 4,2 °C su una soglia di 4 la registrate e la respingete come giudizio, senza aprire
        nulla. Un bancale respinto sì. L'esito resta comunque scritto sulla bolla e nel registro
        di accettazione: dire di no qui non cancella niente, decide solo se aprire una pratica.
      </Callout>

      <Callout kind="warn" titolo="La conferma passa una volta sola">
        La proposta compare al momento del salvataggio. Se dite di no e poi cambiate idea, la NC
        va aperta a mano dal registro: riaprendo la bolla la domanda non ritorna.
      </Callout>

      {/* a7 */}
      <H id="a7" cat="pulizie">7. Il registro da stampare</H>

      <P>
        <Code>Ricevimento materie prime</Code>, nel gruppo Verifiche e controlli, è la stessa
        cosa vista dall'altra parte: non per registrare, ma per <Strong>dimostrare</Strong>.
        Una riga per consegna, con data, fornitore, DDT, numero di articoli ed esito.
      </P>
      <P>
        Si filtra per periodo con <Tasto>Dal</Tasto> e <Tasto>Al</Tasto>, e per esito con{' '}
        <Tasto>Solo conformi</Tasto> / <Tasto>Solo non conformi</Tasto>. Poi{' '}
        <Tasto>Stampa registro</Tasto> produce il <Strong>Registro di accettazione materie
        prime</Strong>: intestazione con i vostri dati, tutte le righe del periodo con i controlli
        eseguiti per esteso, e in fondo lo spazio per la firma del responsabile della verifica.
      </P>

      <Callout kind="tip" titolo="È il documento che chiede l'ispettore">
        Stampatelo a fine mese e fatelo firmare. Il vantaggio rispetto al registro cartaceo non
        è la stampa, è il filtro: <Tasto>Solo non conformi</Tasto> su dodici mesi mostra in un
        colpo se un fornitore vi crea problemi ricorrenti. È anche la base con cui si giustifica
        una riqualifica del fornitore.
      </Callout>

      <P>
        Cliccando una riga si apre la bolla corrispondente, così dal registro si arriva sempre al
        documento originale.
      </P>

      {/* a8 */}
      <H id="a8">Le trappole</H>

      <P>Le cose che fanno perdere tempo, in ordine di frequenza.</P>

      <Callout kind="warn" titolo="Data DDT e data ricevimento non sono la stessa cosa">
        Il lotto interno nasce dalla data del DDT. Cambiarla dopo, su una bolla già salvata, non
        rigenera i lotti già creati: restano come sono. Vale la pena controllarla prima di
        salvare.
      </Callout>

      <Callout kind="warn" titolo="Gli articoli si aggiungono solo alla creazione">
        Riaprendo una bolla salvata potete correggere l'intestazione e i controlli — il pulsante
        si chiama <Tasto>Modifica intestazione</Tasto> — ma la sezione Articoli non ricompare.
        Se avete dimenticato una riga, si aggiunge dal magazzino materie prime alla bolla
        esistente.
      </Callout>

      <Callout kind="warn" titolo="Il fornitore serve sempre, anche in conto lavorazione">
        Fino ad agosto 2026 scegliere il committente escludeva il fornitore. Ora convivono, e il
        fornitore è obbligatorio: se lo lasciate vuoto il programma risponde{' '}
        <em>"Indica il fornitore, cioè chi ti ha consegnato la merce"</em>.
      </Callout>

      <Callout kind="warn" titolo="La tendina degli ingredienti mostra solo gli attivi">
        Se un ingrediente non compare, con ogni probabilità è stato disattivato in anagrafica.
        Non è un problema della bolla.
      </Callout>

      {/* a9 */}
      <H id="a9">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Devo registrare una bolla per ogni consegna, anche piccola?">
          Sì, se contiene qualcosa che finirà in un prodotto. La rintracciabilità si rompe nel
          punto più debole: un sacco di sale non registrato è un anello mancante esattamente
          come mezza tonnellata di carne. Materiali che non toccano l'alimento — detersivi,
          cancelleria — non hanno bisogno di entrare qui.
        </FAQ>

        <FAQ q="Il fornitore non è in anagrafica e ho fretta.">
          Il pulsante <Tasto>Nuovo</Tasto> accanto al campo Fornitore crea l'anagrafica al volo:
          serve solo la ragione sociale e la categoria. Lo completate poi con partita IVA,
          contatti e qualifica quando avete tempo. Meglio un fornitore incompleto che una
          consegna non registrata.
        </FAQ>

        <FAQ q="Ho sbagliato la quantità di un articolo. Posso correggerla?">
          Sì, dal <Code>Magazzino materie prime</Code>: sul lotto c'è il pulsante{' '}
          <Tasto>Correggi</Tasto>, che rimette a posto quantità, unità di misura, lotto del
          fornitore, scadenza e note. La quantità in LabThree è un dato dichiarato, non un
          movimento contabile: si corregge quando ci si accorge dell'errore, senza storni né
          procedure.
        </FAQ>

        <FAQ q="Cosa succede se non compilo nessun controllo?">
          La bolla si salva lo stesso e resta senza esito. La merce entra in magazzino
          regolarmente. Perdete però la prova del controllo al ricevimento, che è quella che
          l'HACCP vi chiede: nel registro di accettazione quella riga comparirà con un trattino
          al posto dell'esito.
        </FAQ>

        <FAQ q="Posso avere modelli diversi per fornitori diversi?">
          I modelli non si legano al fornitore: si sceglie il modello in bolla, ogni volta. Se
          avete più modelli, la tendina <Strong>Modello di controllo</Strong> compare in cima
          alla sezione dei controlli. Con un modello solo viene applicato da sé.
        </FAQ>

        <FAQ q="Ho cambiato un modello. Le vecchie bolle cambiano?">
          No. Ogni bolla conserva i controlli così come sono stati compilati, con le etichette e
          le soglie di allora. È voluto: un registro che cambia retroattivamente non
          dimostrerebbe niente.
        </FAQ>

        <FAQ q="Che differenza c'è fra questa schermata e “Ricevimento materie prime”?">
          Qui si <Strong>registra</Strong>, là si <Strong>legge e si stampa</Strong>. Sono gli
          stessi dati: il registro non è una seconda compilazione, è la vista da ispettore delle
          bolle che avete già inserito.
        </FAQ>

        <FAQ q="Il lotto del fornitore è obbligatorio?">
          No. È fortemente consigliato — senza, risalire a monte diventa difficile — ma ci sono
          consegne che non ne hanno uno. In quel caso a identificare la merce restano il numero
          di DDT e la data, che sono comunque registrati.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Il magazzino materie prime</Strong>: cosa succede ai lotti dopo l'ingresso —
            gli stati, le scadenze, la disponibilità stimata e come si mette un lotto in uso.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
